import { useState, useEffect, useCallback } from 'react';
import {
  leaveMasterApi,
  SEARCH_FIELD_OPTIONS,
  STATUS_OPTIONS,
} from '../api/leaveMasterApi';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';
import { useExportJob } from '../../../../hooks/useExportJob';
import { executePrintReport } from '../../../../utils/printReportUtil';

const REPORT_COLUMNS = [
  { key: 'name', label: 'Leave Name' },
  { key: 'alias', label: 'Leave Code' },
  { key: 'color', label: 'Color' },
  { key: 'status', label: 'Status' },
];

export function useLeaveMasterList() {
  const [searchField, setSearchField] = useState('Name');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active'); // Default to Active as required

  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await leaveMasterApi.getRecords({
        searchField,
        searchText,
        status: statusFilter,
        page,
        pageSize,
      });
      setRecords(res.items || []);
      setTotalCount(res.total_count || 0);
      setTotalPages(res.total_pages || 1);
    } catch (err) {
      console.error(err);
      appToast.error('Failed to load Leave Master records.');
    } finally {
      setIsLoading(false);
    }
  }, [searchField, searchText, statusFilter, page, pageSize]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleSearch = () => {
    setPage(1);
    loadRecords();
  };

  const resetFilters = () => {
    setSearchField('Name');
    setSearchText('');
    setStatusFilter('Active');
    setPage(1);
  };

  const handleStatusTabChange = (key) => {
    setStatusFilter(key);
    setPage(1);
  };

  const handleArchive = async (item) => {
    const isCurrentlyActive = item.status === 'Active';
    const actionText = isCurrentlyActive ? 'Archive' : 'Activate';

    const confirmed = await appConfirm({
      title: `${actionText} Leave Type`,
      message: `Are you sure you want to ${actionText.toLowerCase()} leave type "${item.name}"?`,
      confirmText: actionText,
      confirmVariant: isCurrentlyActive ? 'warning' : 'primary',
    });

    if (!confirmed) return;

    try {
      const res = await leaveMasterApi.archiveRecord(item.id);
      appToast.success(res?.message || `Leave type ${actionText.toLowerCase()}d successfully.`);
      loadRecords();
    } catch (err) {
      console.error(err);
      appToast.error(err?.response?.data?.detail || `Failed to ${actionText.toLowerCase()} leave type.`);
    }
  };

  const handleDelete = async (id, name) => {
    const confirmed = await appConfirm({
      title: 'Delete Leave Type',
      message: `Are you sure you want to delete leave type "${name || id}"? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmVariant: 'danger',
    });

    if (!confirmed) return;

    try {
      await leaveMasterApi.deleteRecord(id);
      appToast.success(`Leave type "${name || id}" deleted successfully.`);
      loadRecords();
    } catch (err) {
      console.error(err);
      appToast.error(err?.response?.data?.detail || 'Failed to delete leave type.');
    }
  };

  const { triggerExport, isExporting } = useExportJob({
    module: 'leave-master',
    jobType: 'EXPORT',
  });

  const tools = [
    { key: 'print', label: 'Print Report', icon: 'print' },
    { key: 'export_excel', label: 'Export Excel', icon: 'description' },
    { key: 'export_pdf', label: 'Export PDF', icon: 'picture_as_pdf' },
  ];

  const handleToolSelect = (toolKey) => {
    const exportFilters = {
      search: searchText,
      searchField,
      status: statusFilter,
    };

    if (toolKey === 'print') {
      executePrintReport({
        title: 'Leave Master Report',
        subtitle: `Status: ${statusFilter} | Total Records: ${totalCount}`,
        columns: REPORT_COLUMNS,
        data: records,
        onExportPdf: () => triggerExport('pdf', exportFilters),
        onExportExcel: () => triggerExport('xlsx', exportFilters),
      });
    } else if (toolKey === 'export_excel') {
      triggerExport('xlsx', exportFilters);
    } else if (toolKey === 'export_pdf') {
      triggerExport('pdf', exportFilters);
    }
  };

  return {
    state: {
      records,
      isLoading,
      isExporting,
      tools,
      searchField,
      searchText,
      statusFilter,
      page,
      pageSize,
      totalCount,
      totalPages,
      searchFieldOptions: SEARCH_FIELD_OPTIONS,
      statusOptions: STATUS_OPTIONS,
    },
    actions: {
      setSearchField,
      setSearchText,
      setStatusFilter,
      handleStatusTabChange,
      setPage,
      setPageSize,
      handleSearch,
      resetFilters,
      handleArchive,
      handleDelete,
      handleToolSelect,
      triggerExport,
      refresh: loadRecords,
    },
  };
}
